import { isEmpty } from "./isEmpty"

type ConvertToLocaleParams = {
  amount: number
  currency_code: string
  minimumFractionDigits?: number
  maximumFractionDigits?: number
  locale?: string
}

/**
 * Map currency codes to sensible Intl locales for formatting.
 * This ensures prices like "500 Kč" instead of "CZK 500.00".
 */
const currencyLocaleMap: Record<string, string> = {
  czk: "cs-CZ",
  eur: "de-DE",
  usd: "en-US",
  gbp: "en-GB",
  rub: "ru-RU",
  uah: "uk-UA",
  pln: "pl-PL",
}

/**
 * The Intl locale for a UI locale — one map instead of the copy in every
 * component that prints a price.
 *
 * It mattered: the checkout sidebar printed the same line twice, once as
 * «150,00 Kč» (a component that passed the locale) and once as «150,00 CZK»
 * (one that forgot), which reads as two different prices.
 */
const uiLocaleMap: Record<string, string> = {
  ru: "ru-RU",
  en: "en-US",
  cs: "cs-CZ",
  uk: "uk-UA",
}

export function intlLocaleFor(uiLocale: string | undefined): string {
  return (uiLocale && uiLocaleMap[uiLocale]) || "en-US"
}

/**
 * A price is formatted by its CURRENCY, not by the reader's language.
 *
 * It used to be the other way round wherever a component had the UI locale to
 * hand, and the two rules met on one row of the checkout: the totals printed
 * «150,00 Kč» (resolved from the currency) and the line beside them «150,00 CZK»
 * (resolved from `ru-RU`, which has no symbol for the koruna) — the same number,
 * read as two prices. Intl offers no way to take the separators from one locale
 * and the symbol from another, so the shop picks the one that matches its price
 * tags: `cs-CZ` for CZK, in all four languages.
 *
 * `locale` is still accepted — and used only for a currency this map does not
 * know, so an exotic currency still formats somehow.
 */
function resolveLocale(locale: string | undefined, currencyCode: string): string {
  return currencyLocaleMap[currencyCode.toLowerCase()] || locale || "en-US"
}

export const convertToLocale = ({
  amount,
  currency_code,
  minimumFractionDigits,
  maximumFractionDigits,
  locale,
}: ConvertToLocaleParams) => {
  return currency_code && !isEmpty(currency_code)
    ? new Intl.NumberFormat(resolveLocale(locale, currency_code), {
        style: "currency",
        currency: currency_code,
        minimumFractionDigits,
        maximumFractionDigits,
      }).format(amount)
    : amount.toString()
}
